import { type Memo, type MemoStatus } from '@kalos/kalos-rpc';
import { Button } from '@kalos/ui';
import { ChevronDown } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type FieldValues, type Path, type PathValue, type UseFormReturn } from 'react-hook-form';
import { type ZodType } from 'zod';

import { DialogOrSheet } from '../../../../../components/DialogOrSheet';
import {
  type FilterDimension,
  getActiveFilterDimensions,
} from '../../../../../components/FilterSheet';
import { ListResultsPhone, ListSearchPhone } from '../../../../../components/ListPhone';
import { ROWS_PER_PAGE } from '../../../../../constants';
import { parseEasternWallClock } from '../../../../../tools/helpers';
import { memoStatusPillClass } from '../../../Memo/memoTableColumnUtils';
import { formatNoteAge } from './noteAge';
import { NoteListRow } from './NoteListRow';

/** Status 1 (Pending) is where every note starts, so only a status past it is shown on the row. */
const PENDING_STATUS_ID = 1;

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

/** "2 photos · 1 video · audio · 3 comments" from the note's files and comments. */
const attachmentSummary = (note: Memo) => {
  const files = note.files?.results ?? [];
  const photos = files.filter((f) => f.mimeType.includes('image') || f.mimeType.includes('pdf'));
  const videos = files.filter((f) => f.mimeType.includes('video'));
  const hasAudio = files.some((f) => f.mimeType.includes('audio'));
  return [
    photos.length > 0 && plural(photos.length, 'photo'),
    videos.length > 0 && plural(videos.length, 'video'),
    hasAudio && 'audio',
    note.commentCount > 0 && plural(note.commentCount, 'comment'),
  ]
    .filter(Boolean)
    .join(' · ');
};

/** The row title: the note's first line, or what it carries when it has no text. */
const noteTitle = (note: Memo) => {
  const firstLine = note.description.trim().split('\n')[0];
  if (firstLine) return firstLine;
  const files = note.files?.results ?? [];
  if (files.some((f) => f.mimeType.includes('audio'))) return 'Audio note';
  if (files.some((f) => f.mimeType.includes('video'))) return 'Video note';
  if (files.length > 0) return 'Photo note';
  return 'Note';
};

type NotesFeedProps<TFilters extends FieldValues> = {
  /** The notes on `currentPage`; the feed keeps the earlier pages it has loaded. */
  notes: Memo[];
  isLoading: boolean;
  isFetching: boolean;
  /** The last load failed. */
  isError: boolean;
  onRetry: () => void;
  totalNotes: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  statuses: MemoStatus[] | undefined;
  /** Opens a note as its own page. */
  onOpenNote: (note: Memo) => void;
  /** The note composer, already bound to its job (or with its own Job field). */
  composer: ReactNode;
  /** Notes from many jobs: each row names its job. */
  showJobOnRows?: boolean;
  /** Where the empty list says notes come from, for example "Notes added to this project". */
  emptyDescription: string;

  // Search and filters. The screen owns the filter model; the feed lays it out.
  filterForm: UseFormReturn<TFilters>;
  /** The free-text field the search bar edits as you type. */
  searchField: Path<TFilters>;
  filterDimensions: ReadonlyArray<FilterDimension<TFilters>>;
  defaultFilterValues: TFilters;
  /** Rules across filter fields, such as a date range that needs both ends. Checked on Apply. */
  filterSchema?: ZodType<TFilters>;
  /** The filter fields, bound to the sheet's staged form. */
  renderFilterFields: (form: UseFormReturn<TFilters>) => ReactNode;
  onClearFilters: () => void;

  // Reports.
  canGenerateReport: boolean;
  generateReportDisabledReason: string | null;
  onGenerateReport: () => void;
  reports: ReactNode;
  /** A saved report is open in the Reports sheet: its own actions replace Generate and Close. */
  isViewingReport?: boolean;
  /** More actions in the Reports sheet, under Generate report, such as Export. */
  reportActions?: ReactNode;
};

/**
 * A phone notes feed, after the field view prototype: the composer, then the notes with a search
 * bar (Filter inside it) and Reports beside the heading. Each row sums up what the note carries
 * (photos, videos, audio, comments); the note's own page shows them. Older notes load as the list
 * scrolls. Shared by a project's Notes tab and /notes, which bring their own filters and composer.
 */
export const NotesFeed = <TFilters extends FieldValues>({
  notes,
  isLoading,
  isFetching,
  isError,
  onRetry,
  totalNotes,
  currentPage,
  onPageChange,
  statuses,
  onOpenNote,
  composer,
  showJobOnRows = false,
  emptyDescription,
  filterForm,
  searchField,
  filterDimensions,
  defaultFilterValues,
  filterSchema,
  renderFilterFields,
  onClearFilters,
  canGenerateReport,
  generateReportDisabledReason,
  onGenerateReport,
  reports,
  isViewingReport = false,
  reportActions,
}: NotesFeedProps<TFilters>) => {
  const [isReportsOpen, setIsReportsOpen] = useState(false);
  const appliedValues = filterForm.watch();
  const searchText = String(appliedValues[searchField] ?? '');
  const activeFilterCount = useMemo(
    () => getActiveFilterDimensions(filterDimensions, appliedValues, defaultFilterValues).length,
    [filterDimensions, appliedValues, defaultFilterValues],
  );
  const isFiltered = activeFilterCount > 0 || !!searchText;

  // Pages loaded so far, by page number. A new filter, or a change in the total (a note added
  // or deleted), starts the feed again from the newest page.
  const [loadedPages, setLoadedPages] = useState<Record<number, Memo[]>>({});
  const resetKey = `${JSON.stringify(appliedValues)}|${totalNotes}`;
  const lastResetKey = useRef(resetKey);
  useEffect(() => {
    if (isLoading || isError) return;
    if (lastResetKey.current !== resetKey) {
      lastResetKey.current = resetKey;
      if (currentPage !== 0) {
        setLoadedPages({});
        onPageChange(0);
        return;
      }
      setLoadedPages({ 0: notes });
      return;
    }
    setLoadedPages((pages) => ({ ...pages, [currentPage]: notes }));
  }, [notes, currentPage, isLoading, isError, resetKey, onPageChange]);

  const feed = useMemo(() => {
    const seen = new Set<number>();
    const rows: Memo[] = [];
    Object.keys(loadedPages)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach((page) =>
        loadedPages[page].forEach((note) => {
          if (seen.has(note.id)) return;
          seen.add(note.id);
          rows.push(note);
        }),
      );
    return rows;
  }, [loadedPages]);
  const lastLoadedPage = Math.max(-1, ...Object.keys(loadedPages).map(Number));
  const hasOlderNotes = (lastLoadedPage + 1) * ROWS_PER_PAGE < totalNotes;
  const isFirstLoad = isLoading && feed.length === 0;
  const loadOlderNotes = useCallback(
    () => onPageChange(lastLoadedPage + 1),
    [onPageChange, lastLoadedPage],
  );

  return (
    <div className="flex flex-col gap-6">
      <section aria-label="Add a note">{composer}</section>

      <section aria-labelledby="notes-feed-heading" className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-2">
          <h2
            id="notes-feed-heading"
            className="text-muted-foreground text-xs font-semibold uppercase tracking-wide"
          >
            Notes <span className="tabular-nums">· {totalNotes}</span>
          </h2>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="-mr-3 text-sm"
            onClick={() => setIsReportsOpen(true)}
          >
            Reports
          </Button>
        </div>
        <ListSearchPhone
          search={{
            value: searchText,
            onChange: (value) =>
              filterForm.setValue(searchField, value as PathValue<TFilters, Path<TFilters>>),
            placeholder: 'Search notes',
          }}
          filters={{
            dimensions: filterDimensions,
            values: appliedValues,
            defaultValues: defaultFilterValues,
            onApply: (values) => filterForm.reset(values),
            sheetTitle: 'Filter notes',
            schema: filterSchema,
            renderFields: renderFilterFields,
          }}
        />

        <ListResultsPhone
          itemLabel="notes"
          showCount={false}
          variant="flush"
          rowsClassName="divide-border divide-y"
          rowCount={feed.length}
          isPending={isFirstLoad}
          isFetching={isFetching}
          isError={isError}
          isFetchingNextPage={isFetching && currentPage > lastLoadedPage}
          hasNextPage={hasOlderNotes}
          onLoadMore={loadOlderNotes}
          onRetry={onRetry}
          onRetryNextPage={onRetry}
          isFiltered={isFiltered}
          onClearFilters={onClearFilters}
          emptyTitle="No notes yet"
          emptyDescription={emptyDescription}
          filteredEmptyTitle="No notes match the selected filters"
          loadingRows={3}
        >
          {feed.map((note) => {
            const status =
              note.statusId > PENDING_STATUS_ID
                ? statuses?.find((s) => s.id === note.statusId)?.statusDescription
                : undefined;
            const chip = status ?? (note.isPinned ? 'Pinned' : undefined);
            const meta = [
              showJobOnRows && note.eventId ? `Job #${note.eventId}` : '',
              note.createdByName,
              formatNoteAge(parseEasternWallClock(note.createdDate)),
              attachmentSummary(note),
            ]
              .filter(Boolean)
              .join(' · ');
            return (
              <NoteListRow
                key={note.id}
                title={noteTitle(note)}
                meta={meta}
                chip={chip}
                chipClassName={status ? memoStatusPillClass(note.statusId) : undefined}
                onSelect={() => onOpenNote(note)}
              />
            );
          })}
        </ListResultsPhone>
      </section>

      <DialogOrSheet open={isReportsOpen} onOpenChange={setIsReportsOpen} title="Reports">
        <div className={isViewingReport ? 'hidden' : 'mb-4 flex flex-col gap-2'}>
          <Button
            type="button"
            className="w-full"
            disabled={!canGenerateReport}
            onClick={() => {
              // Close this sheet first so the report sheet doesn't stack on top of it.
              setIsReportsOpen(false);
              onGenerateReport();
            }}
          >
            Generate report
          </Button>
          {!canGenerateReport && generateReportDisabledReason && (
            <p className="text-muted-foreground text-xs">{generateReportDisabledReason}</p>
          )}
          {reportActions}
        </div>
        {reports}
        <Button
          type="button"
          variant="outline"
          className={isViewingReport ? 'hidden' : 'mt-4 w-full gap-2'}
          onClick={() => setIsReportsOpen(false)}
        >
          <ChevronDown className="h-4 w-4" aria-hidden />
          Close reports
        </Button>
      </DialogOrSheet>
    </div>
  );
};
